import React, { useRef, useState } from 'react';
import { Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { isSafeLinkUrl } from '@/utils/validation';

interface InsertLinkButtonProps {
  value: string;
  onChange: (nextValue: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

export function InsertLinkButton({
  value,
  onChange,
  textareaRef,
}: InsertLinkButtonProps) {
  const [open, setOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [url, setUrl] = useState('');

  // Clicking the trigger blurs the textarea before onOpenChange/handleInsert
  // run, so we can no longer read a live selection there. Capture the caret /
  // selection range on mousedown (which fires while the textarea is still
  // focused). Defaults to the end of the value so a never-touched textarea
  // appends rather than inserting at position 0.
  const capturedSelectionRef = useRef({
    start: value.length,
    end: value.length,
  });

  const urlIsValid = url.length === 0 || isSafeLinkUrl(url);
  const canInsert =
    linkText.trim().length > 0 && url.length > 0 && isSafeLinkUrl(url);

  const captureSelection = () => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? value.length;
    const end = textarea?.selectionEnd ?? value.length;
    // A non-collapsed (highlighted) range is preserved across blur by the
    // browser, so honour it even when focus has moved to the trigger (e.g.
    // after a cancel-then-reopen). A collapsed cursor is only trustworthy
    // while the textarea is focused; otherwise append at the end (a never-
    // focused textarea reports selectionStart 0, which we must not honour).
    if (textarea && (end > start || document.activeElement === textarea)) {
      capturedSelectionRef.current = { start, end };
    } else {
      capturedSelectionRef.current = { start: value.length, end: value.length };
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      const { start, end } = capturedSelectionRef.current;
      setLinkText(value.slice(start, end));
      setUrl('');
    }
    setOpen(nextOpen);
  };

  const handleInsert = () => {
    const textarea = textareaRef.current;
    const { start, end } = capturedSelectionRef.current;
    const markdown = `[${linkText.trim()}](${url.trim()})`;
    const nextValue = value.slice(0, start) + markdown + value.slice(end);
    onChange(nextValue);
    setOpen(false);

    // Restore focus and place the cursor after the inserted markdown once React
    // has re-rendered with the new value (mirrors the editors' drag-drop logic).
    setTimeout(() => {
      const cursor = start + markdown.length;
      textarea?.focus();
      textarea?.setSelectionRange(cursor, cursor);
    }, 0);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onMouseDown={captureSelection}
          onKeyDown={captureSelection}
        >
          <Link2 className="mr-2 h-4 w-4" />
          Link
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Insert link</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="insert-link-text">Link text</Label>
            <Input
              id="insert-link-text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              placeholder="our investor portal"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="insert-link-url">URL</Label>
            <Input
              id="insert-link-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://portal.dff.ventures"
            />
            {!urlIsValid && (
              <p className="text-xs text-destructive">
                Enter a valid http, https, or mailto URL.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleInsert} disabled={!canInsert}>
            Insert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
